import { useEffect, useMemo, useState, useContext } from 'react';
import { EmptyState, Button, GenericLoader, Flex } from '@kiper/ui';
import propTypes from 'prop-types';
import { useQuery } from 'react-apollo';
import { queries } from '@kiper/account-graphql/auth';
import { useTranslation } from 'react-i18next';
import { useSwal } from '@kiper/hooks';
import authContext from '../../services/auth/context';
import ProductCard from '../../components/ProductCard';
import emptyStatePlaceholder from '../../assets/images/not-allowed.svg';
import { adaptApplicationUrl } from './utils';
import * as S from './styles';

function ProductHub({ location, history }) {
  const [t] = useTranslation('product_hub');
  const { toast } = useSwal();

  const { signOut } = useContext(authContext);

  const [state, setState] = useState(location.state);

  const redirectApplication = url => {
    toast.fire({
      title: t('welcome'),
      icon: 'success',
    });
    setTimeout(() => {
      window.location = url;
    }, 1200);
  };

  useEffect(() => {
    if (!state) history.push('/');
  }, []);

  const { data, loading } = useQuery(queries.applications, {
    skip: !state,
    variables: {
      personId: state && state.personId,
    },
  });

  const applications = useMemo(
    () =>
      data &&
      data.applications &&
      data.applications.map(app => ({
        ...app,
        mainUrl: adaptApplicationUrl(app.mainUrl),
      })),
    [data],
  );

  useEffect(() => {
    if (applications && !!applications.length) {
      if (applications.length === 1)
        redirectApplication(applications[0].mainUrl);

      if (state.redirectUri) {
        if (applications.some(app => state.redirectUri.includes(app.mainUrl)))
          redirectApplication(state.redirectUri);
        else {
          setState(prev => ({ ...prev, redirectUri: null }));
          toast.fire({
            title: t('errors.application-not-available'),
            icon: 'error',
          });
        }
      }
    }
  }, [applications]);

  if (
    loading ||
    (applications && applications.length === 1) ||
    (state && state.redirectUri)
  )
    return <GenericLoader />;

  const hasApplications = applications && !!applications.length;

  return (
    <Flex
      gridGap="24px"
      width="100%"
      height="100%"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
    >
      {hasApplications && <S.H3>{t('system-to-access')}</S.H3>}
      <S.Container>
        {hasApplications &&
          applications.map(app => (
            <ProductCard
              key={app.id}
              title={app.name}
              iconUrl={app.icon}
              onClick={() => redirectApplication(app.mainUrl)}
            />
          ))}

        {!hasApplications && (
          <EmptyState
            placeholder={emptyStatePlaceholder}
            text={t('empty-state.title')}
            message={t('empty-state.subtitle')}
          >
            <Button color="primary" onClick={signOut}>
              {t('logout')}
            </Button>
          </EmptyState>
        )}
      </S.Container>
    </Flex>
  );
}

export default ProductHub;
ProductHub.propTypes = {
  location: propTypes.object.isRequired,
  history: propTypes.object.isRequired,
};
